/**
 * Done. / ChiroCPD — Activities Page (/activities)
 *
 * Full activity log with inline form and reflection panels.
 * ?filter=unreflected query param supported (linked from ExportField empty states).
 */

import { useState, useEffect } from 'react';
import { useSearch } from 'wouter';
import { AppLayout } from '@/layouts/AppLayout';
import { ActivityForm } from '@/components/features/ActivityForm';
import { ActivityRow } from '@/components/features/ActivityRow';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Button } from '@/components/ui/Button';
import { CapIndicator } from '@/components/ui/CapIndicator';
import { useActivities } from '@/hooks/useActivities';
import { useCapStatus } from '@/hooks/useCapStatus';
import { api, ApiError } from '@/lib/api';
import type { ActivityType, Activity } from '@/lib/types';

function ActivitiesContent(): React.ReactElement {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const filterUnreflected = params.get('filter') === 'unreflected';

  const { cap } = useCapStatus();
  const { activities, loading, error, retry, createActivity, updateActivity, deleteActivity } =
    useActivities(filterUnreflected ? { status: 'unreflected' } : {});

  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);

  // Fetch activity types
  useEffect(() => {
    async function fetchTypes(): Promise<void> {
      try {
        const types = await api.get<ActivityType[]>('/api/activity-types');
        setActivityTypes(types);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return;
      } finally {
        setTypesLoading(false);
      }
    }
    void fetchTypes();
  }, []);

  async function handleSave(input: Parameters<typeof createActivity>[0]): Promise<void> {
    if (editingActivity) {
      await updateActivity(editingActivity.id, input);
      setEditingActivity(null);
    } else {
      await createActivity(input);
    }
    setShowForm(false);
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page heading */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {filterUnreflected ? 'Activities — Needs Reflection' : 'Activities'}
        </h1>
        {filterUnreflected && (
          <a href="/activities" className="text-sm text-[var(--color-done-600)] hover:underline">
            ← View all activities
          </a>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() => { setShowForm(s => !s); setEditingActivity(null); }}
        >
          {showForm ? 'Cancel' : '+ Log Activity'}
        </Button>
      </div>

      {/* Progress bar */}
      {cap && (
        <section aria-label="Annual CPD hours progress" className="flex flex-col gap-1">
          <ProgressBar
            current={cap.annualHoursLogged}
            max={cap.annualHourCap}
            label={`Annual CPD hours: ${String(cap.annualHoursLogged)} of ${String(cap.annualHourCap)}`}
          />
          <CapIndicator
            current={cap.annualHoursLogged}
            max={cap.annualHourCap}
            unit="hours"
            variant="inline"
          />
        </section>
      )}

      {/* Log Activity Form */}
      {(showForm || editingActivity) && !typesLoading && (
        <ActivityForm
          activityTypes={activityTypes}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingActivity(null); }}
        />
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-[var(--radius-md)] bg-[var(--color-error-100)] px-4 py-3 text-sm text-red-700" role="alert">
          {error}
          <Button variant="ghost" size="sm" onClick={retry} className="ml-3">Retry</Button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <svg className="h-7 w-7 animate-spin text-[var(--color-done-500)]" fill="none" viewBox="0 0 24 24" aria-label="Loading activities…">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && activities.length === 0 && (
        <div className="rounded-[var(--radius-md)] bg-[var(--surface-muted)] px-4 py-8 text-center">
          <p className="text-sm text-[var(--text-secondary)]">
            {filterUnreflected
              ? 'All activities have reflections. Great work!'
              : 'No activities logged yet. Log your first CPD activity to get started.'}
          </p>
        </div>
      )}

      {/* Activity list */}
      {!loading && activities.length > 0 && (
        <section
          aria-label="Activity list"
          className="rounded-[var(--radius-md)] bg-[var(--surface-base)] shadow-sm"
        >
          {activities.map(activity => (
            <ActivityRow
              key={activity.id}
              activity={activity}
              onEdit={a => { setEditingActivity(a); setShowForm(true); }}
              onDelete={deleteActivity}
            />
          ))}
        </section>
      )}
    </div>
  );
}

export function ActivitiesPage(): React.ReactElement {
  return (
    <AppLayout currentRoute="/activities">
      <ActivitiesContent />
    </AppLayout>
  );
}
